import { connectSubscriptions, type SubscriptionClient, type SubscriptionClientOptions } from "@norn/sdk"
import type { WidgetHost } from "./runtime"

/** Lazy, scoped SDK connection shared by every binding in this widget host. */
export function bindingSubscriptions(options: SubscriptionClientOptions, widgetId: string): NonNullable<WidgetHost["subscribeBinding"]> {
  let client: SubscriptionClient | undefined
  let users = 0
  let revoked = false
  const observers = new Set<Parameters<NonNullable<WidgetHost["subscribeBinding"]>>[1]>()
  return (request, observer) => {
    if (revoked) {
      observer.onError(new Error("subscription.authorization_changed"))
      return () => {}
    }
    observers.add(observer)
    client ??= connectSubscriptions(options)
    users++
    let active = true
    const handle = client.subscribeBinding(widgetId, request.bindingId, {
      params: { ...request.query },
      onResult: (result) => { if (active) observer.onResult(result) },
      onInvalidated: () => { if (active) observer.onInvalidated() },
      onEvent: (event) => {
        if (active && event.kind === "closed" && event.reason === "authorization_changed") {
          // No retained projection survives observed revocation. Drafts belong to the renderer.
          revoked = true
          client?.close()
          for (const observing of observers) observing.onError(new Error("subscription.authorization_changed"))
        }
      },
      onRefused: (refusal) => { if (active) observer.onError(new Error(`${refusal.code}: ${refusal.explanation}`)) },
    })
    return () => {
      active = false
      observers.delete(observer)
      handle.unsubscribe()
      if (--users === 0) { client?.close(); client = undefined }
    }
  }
}
